import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { ExerciseArt } from '../components/ExerciseArt';
import { ChapterPager, type RoadStop } from '../components/RoadMap';
import { ScrollingRoad } from '../components/DepthRoad';
import { liveChallenges, getChallenge } from '../data/exercises';
import { PER_CHAPTER, chaptersFor, goalFor } from '../data/road';
import { loadProfile, doneIds, nextOnPath } from '../lib/state';
import { withoutHeld } from '../lib/reveal';
import { useWatchedPosts, printsByExercise } from '../lib/today-waiting';
import type { FeedPost } from '../lib/feed';
import { track } from '../analytics';
import type { Challenge } from '../data/types';
import { useViewed } from '../lib/viewed';
import { useToast } from '../components/Toast';
import { KEEP } from '../data/keep-copy';

/** Exercises done before, most recent first, one row each; open play (Pages) is not a thing to redo from a list, and a retired one or a starter (its own screen, not the runner) cannot be done again. */
function againList(profile: ReturnType<typeof loadProfile>): Challenge[] {
  const seen = new Set<string>();
  const out: Challenge[] = [];
  const history = [...(profile.made || []).map((m) => m.challengeId), ...Object.values(profile.days).map((d) => d.challengeId)].reverse();
  for (const id of history) { const c = getChallenge(id); if (c && !seen.has(id) && c.purpose !== 'open' && !c.retired && !c.starter) { seen.add(id); out.push(c); } }
  return out;
}

/**
 * The crew's picture for each exercise you have turned over, for the road's done stops: what went into the
 * film first (on this device at once, so a picture just kept is there on arrival), then your posts, newest first.
 */
function picturesFrom(posts: FeedPost[] | null, profile: ReturnType<typeof loadProfile>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of withoutHeld(posts || [])) { const url = p.renders?.[0]?.url; if (url && !out[p.challengeId]) out[p.challengeId] = url; }
  for (const pen of Object.values(profile.pencilled || {})) if (pen.pictureUrl && !out[pen.challengeId]) out[pen.challengeId] = pen.pictureUrl;
  return out;
}

/**
 * The road (owner, 2026-09-18; drawn as a map 2026-09-28, K41 RD1): the reset
 * in chapters of seven, each a lesson, ending at what the person said they
 * would like to be. The map is the first thing; below it, the ways off the
 * road that all count: doing an earlier one again, and the whole deck. Open
 * play (Pages) moved to Today on 2026-09-29. None of them move the road; the
 * road is simply the first thing on it you have not done.
 */
export function Path() {
  useViewed('path');
  const nav = useNavigate();
  const arrived = useLocation().state as { landed?: string; link?: 'sent' | 'kept' } | null;
  const landed = arrived?.landed;
  // the email went in before the print turned (screens/Reward.tsx): the road says what happened to it, once
  const { toast, show } = useToast();
  const told = useRef(false);
  useEffect(() => {
    if (told.current || !arrived?.link) return;
    told.current = true;
    show(arrived.link === 'sent' ? KEEP.roadSent : KEEP.roadKept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const profile = loadProfile();
  const done = doneIds(profile);
  const next = nextOnPath(profile);
  const chapters = chaptersFor(profile);
  // the whole road at once, in depth (owner 2026-10-20): the scroll walks you along it, the header says which chapter you are in
  const [cameraAt, setCameraAt] = useState(0);
  const [jump, setJump] = useState<{ to: number } | null>(null);
  const onCamera = useCallback((c: number) => setCameraAt(Math.round(c)), []);
  const chapterN = Math.min(chapters.length, Math.floor(cameraAt / PER_CHAPTER) + 1);
  // your posts, looked at again while the crew are still developing one, so a stop turns from darkroom to dry print on its own
  const posts = useWatchedPosts();
  const pictures = picturesFrom(posts, profile);
  const prints = useMemo(() => (posts ? printsByExercise(posts) : {}), [posts]);
  // the page is named for where the road goes: what they said they would like to be (owner, 2026-09-28)
  const goal = goalFor(profile.dreams);
  const [deckOpen, setDeckOpen] = useState(false);
  const dayDone = (id: string) => Object.entries(profile.days).find(([, d]) => d.challengeId === id)?.[0];
  // most recent first, one row per exercise, the road's next one left out (it is above)
  const again = againList(profile);
  const deck = liveChallenges().filter((c) => c.purpose !== 'open').sort((a, b) => a.minutes - b.minutes || a.title.localeCompare(b.title));
  const go = (c: Challenge, how: 'next' | 'again' | 'pick') => { track('path_chosen', { challengeId: c.id, how }); nav(`/do/${c.id}`, { state: { door: 'front' } }); };
  const all = chapters.flatMap((ch) => ch.stops);
  const stops: RoadStop[] = all.map((c) => ({ c, state: c.id === next.id ? 'next' : done.has(c.id) ? 'done' : 'later', day: dayDone(c.id), picture: pictures[c.id], print: prints[c.id] }));
  const gates = chapters.slice(1).map((ch) => ({ at: (ch.n - 1) * PER_CHAPTER, n: ch.n, lesson: ch.lesson }));
  // stand where the last thing you did is in view with today's ahead of it — or at a print that is waiting to be
  // turned over, so it can be — or at the one just finished when you arrive from its reward
  const nextAt = stops.findIndex((s) => s.state === 'next');
  const landedAt = landed ? stops.findIndex((s) => s.c.id === landed) : -1;
  const dryAt = stops.findIndex((s) => s.state === 'done' && s.print?.state === 'dry');
  let recentAt = -1;
  for (let i = (nextAt >= 0 ? nextAt : stops.length) - 1; i >= 0; i--) if (stops[i].state === 'done') { recentAt = i; break; }
  const start = landedAt >= 0 ? landedAt - 0.15 : dryAt >= 0 ? dryAt + 0.2 : recentAt >= 0 ? recentAt + 0.2 : 0;
  return (
    <Frame tabs theme="fit road-tab" left={<h1 className="road-head">{goal.line}</h1>} right={<ChapterPager n={chapterN} of={chapters.length} onChapter={(n) => setJump({ to: (n - 1) * PER_CHAPTER })} />}>
      <ScrollingRoad stops={stops} start={start} caption={`Chapter ${chapterN} · ${chapters[chapterN - 1].lesson}`} onCamera={onCamera} jump={jump} gates={gates} goal={goal} nextLine="Up next" landed={landed} onStart={go} onTurn={(s) => { track('print_open_from', { from: 'road', challengeId: s.c.id }); nav(s.print!.state === 'dry' ? s.print!.to : '/', { viewTransition: true, state: { back: '/path', landed: s.c.id } }); }} />

      {again.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          <span className="label">Do one again</span>
          {again.slice(0, 6).map((c) => (
            <div className="road-row" key={c.id}>
              <ExerciseArt id={c.id} className="road-img" />
              <span className="road-n">↻</span>
              <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1 }}><b style={{ fontSize: 15 }}>{c.title}</b><span className="hint">{c.minutes} min · fresh deal</span></div>
              <button className="btn small" onClick={() => go(c, 'again')}>Again</button>
            </div>
          ))}
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        <button className="link" style={{ alignSelf: 'flex-start' }} onClick={() => setDeckOpen(!deckOpen)}>{deckOpen ? 'Hide the whole deck' : `The whole deck · ${deck.length} cards`}</button>
        {deckOpen && deck.map((c) => (
          <button key={c.id} className={`road-row ${done.has(c.id) ? 'done' : ''}`} onClick={() => go(c, 'pick')}>
            <span className="road-n">{done.has(c.id) ? '✓' : '·'}</span>
            <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1, textAlign: 'left' }}><b style={{ fontSize: 15 }}>{c.title}</b><span className="hint">{c.minutes} min · {c.artifact}</span></div>
          </button>
        ))}
      </div>
      {toast}
    </Frame>
  );
}
